import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusChip, type StatusChipState } from '@repo/ui/components/status-chip';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components/popover';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { GlobeIcon, InfoIcon } from 'lucide-react';
import {
  isAllowedSourceFilename,
  isHeavyNearEmpty,
  isHintMessage,
  languageName,
  parseNearEmptyHint,
  SOURCE_FILE_LIMITS,
  sourceFileExtension,
  stripHintPrefix,
} from '@repo/shared-types';
import type { BotDto, SourceDto, SourceStatus, SourceType } from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { RenderedModeExplainer, RenderedModeToggle } from '../components/RenderedModeToggle';
import { BILLING_SUMMARY_QUERY_KEY } from '../hooks/useBillingSummary';
import { ApiError, apiFetch } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import {
  ANY_SYNCING,
  applySourceOverrides,
  OVERRIDE_TIMEOUT_MS,
  shouldKeepSyncing,
  type SourceOverride,
  type SourceOverrideBaseline,
} from '../lib/source-overrides';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

type Tab = 'url' | 'sitemap' | 'text' | 'file';

// Task 3 (badge-and-dropzone) — every string the drop zone shows about what it accepts is
// derived from `SOURCE_FILE_LIMITS`, never hand-written: a hardcoded "4MB" keeps passing review
// after someone changes the limit and the zone starts lying to the owner about the API's own
// rule. `FILE_EXTENSION_LABEL`/`FILE_ACCEPT_ATTR` below are the only place this file spells out
// the extension list. The extension-parsing decision itself (`isAllowedSourceFilename`,
// `sourceFileExtension`) is NOT duplicated here — final review of this task moved it into
// `@repo/shared-types` beside `SOURCE_FILE_LIMITS`, so a `.exe` rejected here and a `.exe`
// rejected by the API's 415 are the same function call, not two independently written copies
// that merely agreed by construction.
const FILE_EXTENSION_LABEL = SOURCE_FILE_LIMITS.allowedExtensions.map((ext) => `.${ext}`).join(', ');
const FILE_ACCEPT_ATTR = SOURCE_FILE_LIMITS.allowedExtensions.map((ext) => `.${ext}`).join(',');
const FILE_MAX_SIZE_LABEL = `${SOURCE_FILE_LIMITS.maxBytes / (1024 * 1024)}MB`;

/** Handoff §Status chip: `hint:`-prefixed errors are advisory (amber), not failures — via the
 * one shared convention (`@repo/shared-types`, final review — this file and `SourceDetail.tsx`
 * used to each carry their own copy). */
function chipState(status: SourceStatus, error: string | null): StatusChipState {
  if (status === 'error' && error && isHintMessage(error)) return 'hint';
  return status;
}

/** Fix-wave item 6 — the sized skeleton this table's own "Loading sources…" text loader evaded
 * the redesign's sweep (spec §9: the literal string "Loading…" must not survive anywhere in
 * `apps/dashboard`). Sized to the real table shape below (header + 3 rows) so nothing shifts
 * once the sources land, the same "one shape per real zone" rule every other loading state in
 * this cycle follows. */
function SourcesTableSkeleton({ t }: { t: Catalog }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t.sources.colType}</TableHead>
          <TableHead>{t.sources.colUri}</TableHead>
          <TableHead>{t.sources.colStatus}</TableHead>
          <TableHead>{t.sources.colRenderedMode}</TableHead>
          <TableHead>{t.sources.colPages}</TableHead>
          <TableHead>{t.sources.colLastSynced}</TableHead>
          <TableHead>{t.sources.colActions}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[0, 1, 2].map((i) => (
          <TableRow key={i}>
            <TableCell>
              <SkeletonText className="w-12" />
            </TableCell>
            <TableCell>
              <SkeletonText className="w-48" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-6 w-20 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-9 rounded-full" />
            </TableCell>
            <TableCell>
              <SkeletonText className="w-8" />
            </TableCell>
            <TableCell>
              <SkeletonText className="w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-8 w-32 rounded-md" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function typeLabel(type: SourceType, t: Catalog): string {
  switch (type) {
    case 'url':
      return t.sources.typeUrl;
    case 'sitemap':
      return t.sources.typeSitemap;
    case 'text':
      return t.sources.typeText;
    case 'file':
      return t.sources.typeFile;
  }
}

/** THI-99 (two-tier near-empty signal) — the LIGHT-tier presentation: a handful of near-empty
 * pages on an otherwise normal site doesn't earn a permanent line of amber text in the row (the
 * HEAVY tier below is the one that does); a quiet icon that opens on demand keeps the row calm
 * while still surfacing the fact and the same deep link the heavy tier carries inline. Local
 * `open` state (not lifted into `BotSources`' larger per-row state objects) because nothing
 * outside this one popover ever needs to read or drive it.
 *
 * Same `PopoverTrigger`/icon-button idiom as `RenderedModeExplainer`
 * (`../components/RenderedModeToggle.tsx`) — reused rather than inventing a second one. */
function NearEmptyInfoIcon({
  botId,
  sourceId,
  hintText,
  count,
  t,
}: {
  // `string | undefined`, matching `useParams`'s real inferred type elsewhere in this file
  // (the `<{ botId: string }>` generic narrows the KEY shape, not the value) — every other
  // `to={...}` link in this file tolerates the same looseness via template-literal coercion.
  botId: string | undefined;
  sourceId: string;
  hintText: string;
  count: number;
  t: Catalog;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t.sources.nearEmptyInfoAriaLabel({ count })}
          className="ml-2 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
        >
          <InfoIcon className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-2 text-[12.5px] font-normal normal-case">
        <p>{hintText}</p>
        {/* Closes the popover on click rather than relying only on the navigation it triggers
            to unmount it — the navigation target is a DIFFERENT route (Source Detail), so this
            is a defensive UX nicety for the instant between click and route change, not load-
            bearing for correctness. */}
        <Link
          to={`/bots/${botId}/sources/${sourceId}?filter=near-empty`}
          onClick={() => setOpen(false)}
          className="font-medium text-primary hover:underline"
        >
          {t.sources.reviewPagesLink}
        </Link>
      </PopoverContent>
    </Popover>
  );
}

export function BotSources() {
  const t = useT();
  const { botId } = useParams<{ botId: string }>();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tab>('url');
  const [urlValue, setUrlValue] = useState('');
  const [sitemapValue, setSitemapValue] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [fileTitle, setFileTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Task 3 — the drop zone's own feedback, independent of `addError` (which is reserved for the
  // mutation's response): a rejected extension or an "only the first file" note is decided
  // entirely client-side, before any request exists to fail.
  const [fileDropError, setFileDropError] = useState<string | null>(null);
  const [fileDropNote, setFileDropNote] = useState<string | null>(null);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<Record<string, SourceOverride>>({});
  const [deleteTarget, setDeleteTarget] = useState<SourceDto | null>(null);

  // Fix-wave item 6 — this used to `navigate('/', { replace: true })` on any error here, which
  // was sensible when `/` was the bots list (a 404'd botId really did belong nowhere else) but
  // is a silent, message-less loop now that `/` resolves back to THIS SAME bot's Overview
  // (`resolveLandingPath`): a transient 500 on this one query bounced the visitor to Overview
  // with no explanation and no way to tell it wasn't a click they made. Surfacing the error in
  // place (same pattern `BotOverview.tsx` already uses for its own `botError`) fixes both.
  const { error: botError } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });

  const {
    data: sourcesData,
    isPending: sourcesPending,
    error: sourcesError,
  } = useQuery({
    queryKey: ['bots', botId, 'sources'],
    queryFn: () => apiFetch<SourceDto[]>(`/api/bots/${botId}/sources`),
    enabled: !!botId,
    // See src/lib/source-overrides.ts's module doc comment for why this checks overrides too,
    // not just the raw fetch's own statuses.
    refetchInterval: (query) => (shouldKeepSyncing(query.state.data, overrides) ? 3000 : false),
  });

  const sources = useMemo(
    () => (sourcesData ? applySourceOverrides(sourcesData, overrides) : sourcesData),
    [sourcesData, overrides],
  );

  // THI-99 item 1 — a crawl finishing indexing while this screen is just sitting open (no click
  // from the owner at all) is exactly the case the mutation-level `invalidateUsage()` calls
  // above can't cover: the poller's OWN refetch above is what first learns the new pageCount or
  // ready/error status. Diffs the RAW fetched `sourcesData` (not the override-masked `sources`,
  // which can hide a real status change behind an optimistic one) against what it was the
  // previous time this effect ran. `sourcesData` only gets a new object reference when the
  // fetch result actually differs (React Query's structural sharing), so this effect body only
  // runs on a genuine change, not every 3s tick — and updating `previousSourcesRef` before
  // returning means the SAME transition is never diffed twice, satisfying "at most once per
  // observed change" without any extra bookkeeping.
  const previousSourcesRef = useRef<SourceDto[] | undefined>(undefined);
  useEffect(() => {
    const previous = previousSourcesRef.current;
    previousSourcesRef.current = sourcesData;
    if (!sourcesData || !previous) return;
    const changed = sourcesData.some((source) => {
      const prev = previous.find((p) => p.id === source.id);
      if (!prev) return false; // brand-new row — createSource/uploadFile already invalidated.
      const enteredTerminal =
        (source.status === 'ready' || source.status === 'error') && prev.status !== source.status;
      return enteredTerminal || prev.pageCount !== source.pageCount;
    });
    if (changed) {
      queryClient.invalidateQueries({ queryKey: BILLING_SUMMARY_QUERY_KEY });
    }
  }, [sourcesData, queryClient]);

  function invalidateSources() {
    return queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources'] });
  }

  // Task 2 (dashboard-shell feedback cycle) — a source added, deleted, resynced, or toggled
  // changes Overview's Knowledge card (source/page counts, last-synced, ready/syncing chip)
  // and its stats, but nothing on THIS screen ever read that query, so it went stale until a
  // reload. Same pattern `useAnswerUnanswered` already uses for the rail's pill. Overview
  // isn't mounted from this screen, so invalidating it here is always safe to fire immediately
  // — unlike `invalidateSources()` below, it never races the job poller into stomping an
  // optimistic update (see resyncSource's comment for why that call is NOT always paired here).
  function invalidateOverview() {
    return queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] });
  }

  // THI-99 item 1 — the rail's `UsageBlock` reads this exact query (`useBillingSummary.ts`)
  // and, before this fix, never learned about a source add/delete/resync/toggle that changed
  // the workspace's page count until a hard reload. Same "always safe to fire immediately" call
  // as `invalidateOverview` above — it never races the job poller into stomping an optimistic
  // update, because nothing about this screen writes optimistic DATA into the billing-summary
  // cache the way `overrides` does for `sources`.
  function invalidateUsage() {
    return queryClient.invalidateQueries({ queryKey: BILLING_SUMMARY_QUERY_KEY });
  }

  const createSource = useMutation({
    mutationFn: (payload: { type: 'url' | 'sitemap' | 'text'; uri: string; content?: string }) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: async () => {
      await Promise.all([invalidateSources(), invalidateOverview(), invalidateUsage()]);
      setAddError(null);
      setUrlValue('');
      setSitemapValue('');
      setTextTitle('');
      setTextContent('');
    },
    onError: (err: Error) => setAddError(err.message),
  });

  const uploadFile = useMutation({
    mutationFn: (form: FormData) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/upload`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: async () => {
      await Promise.all([invalidateSources(), invalidateOverview(), invalidateUsage()]);
      setAddError(null);
      setFile(null);
      setFileTitle('');
      setFileDropError(null);
      setFileDropNote(null);
    },
    onError: (err: Error) => setAddError(err.message),
  });

  // Task 3 — the single decision point for "what file did the owner just hand us," fed by both
  // the drop zone's `drop` event and the native input's own `change` event (click-to-browse):
  // one rule, so a click-picked file and a dropped file are always judged the same way. Only the
  // FIRST file is ever kept — several-at-once says so instead of silently discarding the rest
  // (ambiguity resolution 5). Size, then extension — same order and the same two gates
  // `apps/api/src/routes/sources.ts` enforces server-side — are both rejected right here, before
  // any request exists to reject them (resolution 4): the zone already states the size cap, so
  // silently accepting an oversized file and only failing it after the API's own round trip
  // would be the exact "wait to be told 'not a .pdf'" problem this task exists to avoid, just for
  // bytes instead of an extension.
  function applyFileSelection(files: FileList | File[] | null) {
    const list = files ? Array.from(files) : [];
    const first = list[0];
    if (!first) return;
    const rest = list.slice(1);
    if (first.size > SOURCE_FILE_LIMITS.maxBytes) {
      setFile(null);
      setFileDropNote(null);
      setFileDropError(t.sources.fileTooLarge({ maxSize: FILE_MAX_SIZE_LABEL }));
      return;
    }
    if (!isAllowedSourceFilename(first.name)) {
      setFile(null);
      setFileDropNote(null);
      const ext = sourceFileExtension(first.name);
      setFileDropError(t.sources.fileTypeNotSupported({ ext, extensions: FILE_EXTENSION_LABEL }));
      return;
    }
    setFile(first);
    setFileDropError(null);
    setFileDropNote(
      rest.length > 0
        ? t.sources.filesDroppedNote({ name: first.name, count: rest.length })
        : null,
    );
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    applyFileSelection(e.target.files);
  }

  // Fix round (Minor 5, relocated during final polish) — without this, re-picking the SAME
  // filename through the OS dialog (after a successful upload reset `file` to null, or after a
  // rejected pick) never fires a second `change` event, because the input's own `value` still
  // holds that filename and the browser only fires `change` when it differs. This used to run in
  // `handleFileInputChange` instead, AFTER every pick — which left the native input reporting
  // "no file chosen" permanently, contradicting its own accessible description
  // (`file-drop-prompt`, which still shows the picked filename) for a screen-reader user.
  // Clearing it on `click`, immediately before the OS picker opens, still decouples "what React
  // thinks is selected" from "what the input's value is" (so the next pick — same file or not —
  // always fires `change`), but only for the instant between click and pick, not forever after.
  // Nothing in this file reads `input.files` directly, so there's no functional risk either way.
  function handleFileInputClick(e: React.MouseEvent<HTMLInputElement>) {
    e.currentTarget.value = '';
  }

  function handleFileDragOver(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    // Fix round (Minor 7) — without this the OS/browser drag cursor is whatever it guesses
    // (often a "not allowed" glyph over a non-input element), which visually contradicts a zone
    // that's telling the owner a drop is welcome here.
    //
    // Fix round (final polish) — but only when the drag actually carries a file. Dragging
    // selected text or a link across this zone has `dataTransfer.types` without `'Files'` in it,
    // and unconditionally promising 'copy' told the OS a drop was welcome for a drag that
    // `applyFileSelection` would silently no-op on release (an empty `FileList`, no error, no
    // note — the zone looked broken). `'none'` for the non-file case keeps the cursor honest;
    // `handleFileDrop` below shows a real message if one is dropped anyway.
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes('Files') ? 'copy' : 'none';
    if (!fileDragOver) setFileDragOver(true);
  }

  // Fix round (Important 1) — `dragleave` fires on this label every time the pointer crosses
  // from its padding onto one of its own `<p>` children (the prompt/caption text sits dead
  // centre, exactly where a dragged file is aimed), and unlike `onMouseLeave`, React's
  // `onDragLeave` is NOT relatedTarget-polyfilled to skip child transitions. Unconditionally
  // clearing `fileDragOver` here strobed the highlight on the ordinary path: dragOver → child
  // dragleave clears it → the next dragover (at least every ~350ms per spec) restores it. The
  // `contains` guard only clears the state when the pointer has left the zone for something that
  // ISN'T one of its own descendants — an ancestor-relationship check, not an identity check, so
  // it keeps working if a future edit adds an icon or wraps the text differently.
  function handleFileDragLeave(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setFileDragOver(false);
  }

  function handleFileDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setFileDragOver(false);
    // Fix round (final polish) — a drag with no file in it (dragged text, a link) has an empty
    // `dataTransfer.files` at drop time, same as a real browser reports it; `applyFileSelection`
    // would silently return on its own `!first` guard, clearing the highlight with no feedback
    // at all. Checked directly on `.files` here (not `.types`, which `handleFileDragOver` uses)
    // because `.files` is exactly what determines whether there's anything to select — one fewer
    // level of indirection for the check that actually gates `applyFileSelection`. Same message
    // slot (`fileDropError`) the extension/size rejections below already use.
    if (e.dataTransfer.files.length === 0) {
      setFileDropNote(null);
      setFileDropError(t.sources.fileDropNotAFile);
      return;
    }
    applyFileSelection(e.dataTransfer.files);
  }

  // The override's baseline is whatever the row ACTUALLY was right before the mutation —
  // never an assumed status. Resync and the rendered-mode toggle are both clickable from
  // 'error' (the near-empty-pages hint's whole point is to steer the owner into flipping this
  // exact switch from that exact state), not just 'ready', so hardcoding one starting status
  // broke the override for every other one. Falls back to a status that can never match a real
  // fetch (forcing the override permanently "active" is the safe failure mode) only if the row
  // is somehow missing from the cache the button that triggered this was rendered from.
  function captureBaseline(sourceId: string): SourceOverrideBaseline {
    const current = sourcesData?.find((s) => s.id === sourceId);
    if (current) {
      return { status: current.status, lastSyncedAt: current.lastSyncedAt, error: current.error };
    }
    // Sentinel values a real fetch can never produce (`lastSyncedAt`/`error` are always either
    // `null` or a genuine timestamp/message) — guarantees `isOverrideUnresolved` stays true
    // instead of coincidentally matching whatever the row turns out to be.
    return { status: 'pending', lastSyncedAt: '__unknown__', error: '__unknown__' };
  }

  // Found live verifying the fix above from an `error` row: `OVERRIDE_TIMEOUT_MS`'s display
  // deadline only ever gets re-checked when something re-renders this component with a fresh
  // `Date.now()` — and React Query's default `structuralSharing` means a background refetch
  // that comes back byte-identical (a source pointed at a URL that fails the same way twice,
  // reproduced live with a `.invalid` TLD) reuses the same `sourcesData` reference, so the
  // `useMemo` below never recomputes and nothing ever re-checks the clock. A real `setTimeout`,
  // independent of query data, is what actually guarantees a re-render happens at the right
  // moment. Keyed by `startedAt` so a second resync/toggle on the same row before the first
  // timer fires can't have its own (later) override affected by the earlier timer.
  //
  // Fix-round-2 review (Important): this must NOT delete the override — deleting it would drop
  // it from `shouldKeepSyncing`'s view too, silently giving up on polling at exactly the
  // deadline meant only to retire the `pending` DISPLAY (see `OVERRIDE_TIMEOUT_MS`'s own doc
  // comment for why that's the original bug again, just gated behind queue backlog instead of
  // a click). So the override entry is left completely untouched — the callback only replaces
  // the top-level `overrides` object with a new reference (same contents) purely to bust the
  // `useMemo`'s dependency check below and force `applySourceOverrides` to re-run with a fresh
  // `now`; `isOverrideUnresolved`/`shouldKeepSyncing` never even see this timer fire.
  const overrideTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(
    () => () => {
      Object.values(overrideTimeouts.current).forEach(clearTimeout);
    },
    [],
  );
  function scheduleOverrideExpiry(sourceId: string, startedAt: number) {
    clearTimeout(overrideTimeouts.current[sourceId]);
    overrideTimeouts.current[sourceId] = setTimeout(() => {
      setOverrides((prev) => {
        const current = prev[sourceId];
        if (!current || current.startedAt !== startedAt) return prev;
        return { ...prev };
      });
    }, OVERRIDE_TIMEOUT_MS + 250);
  }

  // Resync sets a 'pending' override the instant it's clicked (see src/lib/source-overrides.ts
  // for why a cache write alone isn't enough) and clears it on error; the success path
  // deliberately does nothing to the override or the sources query — the override just stops
  // being unresolved (see `isOverrideUnresolved`) once a fetch shows the job actually moved the
  // row, whether that fetch comes from the `refetchInterval` above or from something else
  // entirely.
  const resyncSource = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sourceId}/resync`, { method: 'POST' }),
    onMutate: async (sourceId) => {
      const startedAt = Date.now();
      setOverrides((prev) => ({
        ...prev,
        [sourceId]: { status: 'pending', baseline: captureBaseline(sourceId), startedAt },
      }));
      scheduleOverrideExpiry(sourceId, startedAt);
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
    },
    onSuccess: () => Promise.all([invalidateOverview(), invalidateUsage()]),
    onError: (err: Error, sourceId) => {
      clearTimeout(overrideTimeouts.current[sourceId]);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
      setRowErrors((prev) => ({ ...prev, [sourceId]: err.message }));
    },
  });

  // DELETE (apps/api/src/routes/sources.ts) is an atomic compare-and-swap: by the time this
  // resolves, the row's `status` is ALREADY `deleting` in the database, and the response carries
  // that real `SourceDto` back — no more `{ queued: true }` placeholder to paper over. So unlike
  // resync/toggle-rendered-mode above, delete needs no override: invalidating the sources query
  // fetches the server's own truth (`status: 'deleting'`), which `StatusChip` and `ANY_SYNCING`
  // (`src/lib/source-overrides.ts`) already know how to render and keep polling for respectively
  // — the existing `refetchInterval` above takes it from there and polls the row away once the
  // teardown job (which deletes the row for real) finishes. Confirmed via dialog already gates
  // the destructive intent, so nothing here needs an optimistic anything.
  const deleteSource = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sourceId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => Promise.all([invalidateSources(), invalidateOverview(), invalidateUsage()]),
    onError: (err: Error, sourceId) => {
      setRowErrors((prev) => ({ ...prev, [sourceId]: err.message }));
    },
  });

  const toggleRenderedMode = useMutation({
    mutationFn: ({ sourceId, value }: { sourceId: string; value: boolean }) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sourceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renderedMode: value }),
      }),
    // Same override as resyncSource (a PATCH here enqueues the exact same resync chain), plus
    // the target `renderedMode` so the switch shows where it's headed without waiting on the
    // round trip. Once the override settles, `applySourceOverrides` stops forcing this value
    // too — the real fetched `renderedMode` will already agree in the normal single-actor case
    // (the PATCH persists it synchronously before the job is even enqueued), so this only ever
    // matters for the brief window before that response lands.
    onMutate: async ({ sourceId, value }) => {
      const startedAt = Date.now();
      setOverrides((prev) => ({
        ...prev,
        [sourceId]: { status: 'pending', baseline: captureBaseline(sourceId), renderedMode: value, startedAt },
      }));
      scheduleOverrideExpiry(sourceId, startedAt);
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
    },
    onSuccess: () => Promise.all([invalidateOverview(), invalidateUsage()]),
    onError: (err: Error, { sourceId }) => {
      clearTimeout(overrideTimeouts.current[sourceId]);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[sourceId];
        return next;
      });
      const message =
        err instanceof ApiError && err.status === 409
          ? t.sources.syncInProgressError
          : err.message;
      setRowErrors((prev) => ({ ...prev, [sourceId]: message }));
    },
  });

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAddError(null);
    if (activeTab === 'url') {
      if (!urlValue.trim()) return;
      createSource.mutate({ type: 'url', uri: urlValue.trim() });
    } else if (activeTab === 'sitemap') {
      if (!sitemapValue.trim()) return;
      createSource.mutate({ type: 'sitemap', uri: sitemapValue.trim() });
    } else if (activeTab === 'text') {
      if (!textTitle.trim() || !textContent.trim()) return;
      createSource.mutate({ type: 'text', uri: textTitle.trim(), content: textContent });
    } else {
      if (!file) return;
      const form = new FormData();
      form.set('file', file);
      if (fileTitle.trim()) form.set('title', fileTitle.trim());
      uploadFile.mutate(form);
    }
  }

  const isSubmitting = createSource.isPending || uploadFile.isPending;

  const TABS: { id: Tab; label: string }[] = [
    { id: 'url', label: t.sources.typeUrl },
    { id: 'sitemap', label: t.sources.typeSitemap },
    { id: 'text', label: t.sources.typeText },
    { id: 'file', label: t.sources.typeFile },
  ];

  // Task 7 header subline (handoff §7's own worked example: "3 sources · 184 pages · last
  // synced 2 hours ago") — built from the same `sources` list the table below renders, not a
  // new fetch. `relativeTime` (not spelled-out words) to match the table's own "Last synced"
  // column.
  const sourcesSubtitle = (() => {
    if (!sources || sources.length === 0) return undefined;
    const pageCount = sources.reduce((sum, s) => sum + s.pageCount, 0);
    const lastSyncedAt = sources.reduce<string | null>((latest, s) => {
      if (!s.lastSyncedAt) return latest;
      return !latest || s.lastSyncedAt > latest ? s.lastSyncedAt : latest;
    }, null);
    return t.sources.sourcesSubtitle({
      n: sources.length,
      pageCount,
      lastSynced: relativeTime(lastSyncedAt, t.time),
    });
  })();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t.sources.pageTitle} subtitle={sourcesSubtitle} />

      {botError && (
        <p className="text-sm text-destructive" role="alert">
          {t.sources.botLoadError({ message: botError.message })}
        </p>
      )}

      <div className="rounded-xl border p-4">
        <div className="mb-4 flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                setAddError(null);
                // Fix round (Minor 6) — leaving the File tab and coming back must not re-show a
                // stale rejection/note from before the tab switch, the same "clear on tab
                // change" rule `addError` right above already follows.
                setFileDropError(null);
                setFileDropNote(null);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleAddSubmit} className="flex flex-col gap-3">
          {activeTab === 'url' && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="url-input">{t.sources.urlFieldLabel}</Label>
              <Input
                id="url-input"
                type="url"
                placeholder={t.sources.urlPlaceholder}
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
              />
            </div>
          )}
          {activeTab === 'sitemap' && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="sitemap-input">{t.sources.sitemapFieldLabel}</Label>
              <Input
                id="sitemap-input"
                type="url"
                placeholder={t.sources.sitemapPlaceholder}
                value={sitemapValue}
                onChange={(e) => setSitemapValue(e.target.value)}
              />
            </div>
          )}
          {activeTab === 'text' && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="text-title">{t.sources.textTitleFieldLabel}</Label>
                <Input
                  id="text-title"
                  value={textTitle}
                  onChange={(e) => setTextTitle(e.target.value)}
                  placeholder={t.sources.textTitlePlaceholder}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="text-content">{t.sources.textContentFieldLabel}</Label>
                <textarea
                  id="text-content"
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  rows={5}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  placeholder={t.sources.textContentPlaceholder}
                />
              </div>
            </>
          )}
          {activeTab === 'file' && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="file-input">{t.sources.fileFieldLabel}</Label>
                {/* Handoff's existing dashed-zone idiom (FirstRun.tsx, Bots.tsx empty states):
                    `rounded-lg border border-dashed p-6 text-center`, reused rather than
                    invented fresh. Always visible — not conjured only mid-drag — because a zone
                    that only appears once you're already dragging can't tell you dragging was
                    possible (Johnny's own complaint: a tiny input nobody could guess was a drop
                    target). The native input stays the mechanism (`sr-only`, inside this
                    `<label>`), so click-to-browse and keyboard (native input's own Enter/Space
                    "open picker" behavior) both keep working unmodified; `has-[:focus-visible]`
                    below gives that keyboard focus a visible ring on the zone itself, matching
                    every other focus-visible ring in this file's own inputs.

                    Fix round (Minor 4) — no `htmlFor` here: the `<Label>` above already
                    associates "File" with `#file-input` via `for`, and this element ALSO wraps
                    the input (an implicit label regardless of `for`), so having both meant the
                    input's accessible name concatenated two labels' text with no guaranteed
                    separator ("...click to browse.pdf, .docx..."). The prompt/caption below are
                    `aria-hidden` (excluded from that implicit label's content) and instead wired
                    to the input via `aria-describedby`, which explicitly space-joins the
                    referenced elements and is well-supported even though its targets are
                    `aria-hidden` — the name stays the single clean "File" from the `<Label>`
                    above, the description carries what the zone actually says. */}
                <label
                  onDragOver={handleFileDragOver}
                  onDragLeave={handleFileDragLeave}
                  onDrop={handleFileDrop}
                  className={`flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed p-6 text-center transition-colors has-[:focus-visible]:border-ring has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50 ${
                    fileDragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept={FILE_ACCEPT_ATTR}
                    onClick={handleFileInputClick}
                    onChange={handleFileInputChange}
                    aria-describedby="file-drop-prompt file-drop-caption"
                    className="sr-only"
                  />
                  <p id="file-drop-prompt" aria-hidden="true" className="text-sm font-medium">
                    {file ? file.name : t.sources.fileDropPrompt}
                  </p>
                  <p
                    id="file-drop-caption"
                    aria-hidden="true"
                    className="text-xs text-muted-foreground"
                  >
                    {t.sources.fileDropCaption({
                      extensions: FILE_EXTENSION_LABEL,
                      maxSize: FILE_MAX_SIZE_LABEL,
                    })}
                  </p>
                </label>
                {fileDropError && (
                  <p className="text-xs text-destructive" role="alert">
                    {fileDropError}
                  </p>
                )}
                {/* Fix round (Minor 7) — the reject branch above already nulls `fileDropNote`,
                    so `!fileDropError` here was always true whenever `fileDropNote` was truthy;
                    the guard was dead. */}
                {fileDropNote && (
                  <p className="text-xs text-muted-foreground" role="status">
                    {fileDropNote}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="file-title">{t.sources.fileTitleFieldLabel}</Label>
                <Input
                  id="file-title"
                  value={fileTitle}
                  onChange={(e) => setFileTitle(e.target.value)}
                  placeholder={t.sources.fileTitlePlaceholder}
                />
              </div>
            </>
          )}

          {addError && <p className="text-sm text-destructive">{addError}</p>}

          <div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t.sources.addingCta : t.sources.addSourceCta}
            </Button>
          </div>
        </form>
      </div>

      {sourcesPending && <SourcesTableSkeleton t={t} />}
      {sourcesError && <p className="text-destructive">{sourcesError.message}</p>}

      {sources && sources.length === 0 && (
        <EmptyState
          icon={GlobeIcon}
          title={t.sources.emptyTitle}
          description={t.sources.emptyDescription}
        />
      )}

      {sources && sources.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.sources.colType}</TableHead>
              <TableHead>{t.sources.colUri}</TableHead>
              <TableHead>{t.sources.colStatus}</TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-1">
                  {t.sources.colRenderedMode}
                  <RenderedModeExplainer t={t} />
                </span>
              </TableHead>
              <TableHead>{t.sources.colPages}</TableHead>
              <TableHead>{t.sources.colLastSynced}</TableHead>
              <TableHead>{t.sources.colActions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources.map((source) => {
              const chip = chipState(source.status, source.error);
              const syncing = ANY_SYNCING.includes(source.status);
              // THI-99 (two-tier near-empty signal) — `parseNearEmptyHint` returns non-null for
              // ANY near-empty hint variant (old copy or either current tier), so `null` here
              // already answers "is this even the near-empty hint at all" — no separate
              // `isNearEmptyHint` check needed alongside it.
              const nearEmptyParsed = source.error ? parseNearEmptyHint(source.error) : null;
              const nearEmptyHeavy = nearEmptyParsed
                ? isHeavyNearEmpty(nearEmptyParsed.count, nearEmptyParsed.total)
                : false;
              return (
                <TableRow key={source.id}>
                  <TableCell>{typeLabel(source.type, t)}</TableCell>
                  <TableCell className="max-w-64 truncate" title={source.uri}>
                    {/* Task 5 (source-detail-diagnostics) — the row's own link to what got
                        indexed and what did not, and why (SourceDetail.tsx). The row itself
                        keeps its existing actions; this just adds a way in. */}
                    <Link to={`/bots/${botId}/sources/${source.id}`} className="hover:underline">
                      {source.uri}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusChip state={chip} label={t.status[chip]} />
                    {/* content-language design (2026-08-22, THI-119) — a muted chip naming the
                        majority detected language, only when at least one is known
                        (`source.languages` is `[]`, never omitted, on an undetected/not-yet-
                        crawled source — see `SourceDto.languages`'s own doc comment). Just the
                        top (already-sorted-desc) entry; the full breakdown is one click away
                        on Source Detail (`SourceDetail.tsx`'s header line). `source.pageCount`
                        (already rendered in its own column) stands in for "total pages", per
                        this task's own instruction, rather than summing every known-language
                        count here — the two can legitimately differ when some pages went
                        undetected. */}
                    {source.languages.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-2 font-normal normal-case"
                        title={`${languageName(source.languages[0]!.code)} · ${source.languages[0]!.pages} of ${source.pageCount} pages`}
                      >
                        {languageName(source.languages[0]!.code)}
                      </Badge>
                    )}
                    {/* THI-99 — ONLY the near-empty/rendered-mode nudge (`nearEmptyParsed`
                        non-null) gets special treatment; every other hint (rate-limit/deadline
                        truncation, off-site redirects, a future page-cap hint,
                        `verify_indexing`'s own hints) stays the plain muted span — there's
                        nothing on Source Detail those OTHER hints could pre-filter to.

                        Two-tier near-empty signal: HEAVY (half or more of the emitted pages
                        looked empty) keeps the inline deep-link text from before, now
                        warning-toned (`var(--st-paused)`, the SAME amber `StatusChip`'s own
                        `'hint'` state uses — see that component's doc comment — rather than a
                        one-off color, so this reads as "the same severity as the chip's amber",
                        not a fourth distinct hue this dashboard has never used). LIGHT (fewer
                        than half) drops the inline text entirely in favor of the quiet
                        `NearEmptyInfoIcon` — a few thin pages on an otherwise normal site
                        doesn't earn a permanent line of amber in the row. */}
                    {source.error &&
                      (nearEmptyParsed ? (
                        nearEmptyHeavy ? (
                          <Link
                            to={`/bots/${botId}/sources/${source.id}?filter=near-empty`}
                            className="ml-2 text-xs font-medium underline decoration-dotted"
                            style={{ color: 'var(--st-paused)' }}
                            title={source.error}
                          >
                            {stripHintPrefix(source.error)}
                          </Link>
                        ) : (
                          <NearEmptyInfoIcon
                            botId={botId}
                            sourceId={source.id}
                            hintText={stripHintPrefix(source.error)}
                            count={nearEmptyParsed.count}
                            t={t}
                          />
                        )
                      ) : (
                        <span className="ml-2 text-xs text-muted-foreground" title={source.error}>
                          {stripHintPrefix(source.error)}
                        </span>
                      ))}
                  </TableCell>
                  <TableCell>
                    {(source.type === 'url' || source.type === 'sitemap') && (
                      <RenderedModeToggle
                        checked={source.renderedMode}
                        disabled={syncing || toggleRenderedMode.isPending}
                        onConfirm={(value) =>
                          toggleRenderedMode.mutate({ sourceId: source.id, value })
                        }
                        t={t}
                      />
                    )}
                  </TableCell>
                  <TableCell>{source.pageCount}</TableCell>
                  <TableCell>{relativeTime(source.lastSyncedAt, t.time)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={syncing || resyncSource.isPending}
                          onClick={() => resyncSource.mutate(source.id)}
                        >
                          {t.sources.resyncCta}
                        </Button>
                        <Button
                          variant="destructive-soft"
                          size="sm"
                          // Deliberately NOT `syncing` — deleting mid-crawl is a supported
                          // escape hatch (fix round 1, Important 1): `DELETE /:sid` has no
                          // in-flight guard, unlike `resync`/`PATCH`, which both 409 with
                          // `SOURCE_DELETING`. An owner who realizes 20 seconds into a
                          // 4,000-page crawl that it's the wrong domain, or a workspace at its
                          // source cap trying to free a slot, must still be able to delete
                          // without waiting out the crawl (or the ~30s a resync override keeps
                          // `syncing` true even after a resync has already failed). Only
                          // `status === 'deleting'` itself disables this — clicking it again
                          // would enqueue nothing and just confirm what's already true.
                          disabled={source.status === 'deleting' || deleteSource.isPending}
                          onClick={() => setDeleteTarget(source)}
                        >
                          {t.sources.deleteCta}
                        </Button>
                      </div>
                      {rowErrors[source.id] && (
                        <span className="text-xs text-destructive">{rowErrors[source.id]}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Same Dialog idiom as BotSettings.tsx's DangerZone (delete bot) and
          AdminJobDetail.tsx's cancel-job confirm — no alert-dialog component exists in
          packages/ui, and this screen shouldn't invent a second confirmation idiom. */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent closeLabel={t.common.close}>
          <DialogHeader>
            <DialogTitle>{t.sources.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>
              {t.sources.deleteConfirmDescriptionPrefix}
              <span className="font-medium text-foreground">{deleteTarget?.uri}</span>
              {t.sources.deleteConfirmDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              {t.sources.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (!deleteTarget) return;
                deleteSource.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {t.sources.deleteCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
